<template>
    <div class="reservation-body px-3">
        <form 
            id="course-prices-form" 
            class="course-prices-form" 
            :action="saveRequestUrl" 
            method="get"
            autocomplete="off"
        >
            <input type="hidden" name="custom_residence[description]" v-model="customResidence.description">
            <input type="hidden" name="custom_residence[price]" v-model="customResidence.price">

            <span 
                v-if="(Date.parse(startDate) + courseWeeks * 7 * 24 * 60 * 60 * 1000) > Date.parse('01/01/2025')" 
                class="text-danger"
            >
                {{ tra.prices_may_vary }}
            </span>
            <br>

            <!-- Start Date Section -->
            <StartDatePicker
                :start-date="startDate"
                :disabled-vacation-dates="disabledVacationDates"
                :post-errors="postErrors"
                :tra="tra"
                @update-start-date="$emit('update-start-date', $event)"
            />

            <!-- Course Weeks Section -->
            <CourseWeeksSelector
                :course-weeks="courseWeeks"
                :weeks-count="weeksCount"
                :tra="tra"
                @update-course-weeks="$emit('update-course-weeks', $event)"
            />

            <!-- Residence Section -->
            <ResidenceSection
                v-if="residencesObj && residencesObj.length > 0"
                :data-obj="dataObj"
                :residence-weeks="residenceWeeks"
                :weeks-count="weeksCount"
                :post-errors="postErrors"
                :tra="tra"
                @update-residence-weeks="$emit('update-residence-weeks', $event)"
                @institute-vacation-check="$emit('institute-vacation-check')"
            />

            <!-- Airport Section -->
            <AirportSelector
                v-if="airportsObj && airportsObj.length > 0"
                :data-obj="dataObj"
                :airports-obj="airportsObj"
                :airport-id="airportId"
                :post-errors="postErrors"
                :tra="tra"
                @update-airport-id="$emit('update-airport-id', $event)"
            />

            <!-- Insurance Section -->
            <InsuranceSection
                v-if="dataObj.insurance_price != 0"
                :data-obj="dataObj"
                :insurance-checker="insuranceChecker"
                :post-errors="postErrors"
                :tra="tra"
                @update-insurance-checker="$emit('update-insurance-checker', $event)"
            />

            <!-- Visa Section -->
            <VisaSection
                v-if="dataObj.visa != 0 && appName == 'sat-edu'"
                :data-obj="dataObj"
                :visa-checker="visaChecker"
                :is-admin="isAdmin"
                :post-errors="postErrors"
                :tra="tra"
                @update-visa-checker="$emit('update-visa-checker', $event)"
            />

            <!-- Additional Costs -->
            <AdditionalCosts
                :data-obj="dataObj"
                :tra="tra"
            />

            <!-- Coupon Section -->
            <CouponSection
                v-if="dataObj.visa != 0 && appName == 'sat-edu'"
                :have-coupon="haveCoupon"
                :coupon-code="couponCode"
                :coupon-status="couponStatus"
                :tra="tra"
                @update-have-coupon="$emit('update-have-coupon', $event)"
                @update-coupon-code="$emit('update-coupon-code', $event)"
                @apply-coupon="$emit('apply-coupon')"
                @remove-coupon="$emit('remove-coupon')"
            />

            <!-- Total Price for Langtour -->
            <div v-if="appName == 'langtour'">
                <div class="reservation-heading px-3 d-flex justify-content-between align-items-center">
                    <h6 class="font-weight-bold text-main-color mb-0">{{ tra.total_price }}</h6>
                    <h5 class="text-main-color m-0">
                        {{ dataObj.total_price || 0 }}
                        <span class="small text-dark">
                            {{ (dataObj.currency?.code || 'SAR') == 'SAR' ? 'ر.س' : (dataObj.currency?.code || 'SAR') }}
                        </span>
                    </h5>
                </div>
                <hr class="mx-3 my-2">
            </div>

            <!-- Students Number -->
            <div v-if="isAdmin == '1'" class="form-group">
                <label>{{ tra.students_number }}</label>
                <input 
                    type="number" 
                    name="students_number" 
                    :value="studentsNumber"
                    @input="$emit('update-students-number', $event.target.value)"
                    class="form-control rounded-10 border"
                >
            </div>

            <!-- Action Buttons -->
            <ActionButtons
                :app-name="appName"
                :is-admin="isAdmin"
                :request-id="requestId"
                :add-new-student-request-obj="addNewStudentRequestObj"
                :tra="tra"
                @invoice="$emit('invoice', $event)"
                @update-compare-offer="$emit('update-compare-offer', $event)"
                @save-new-student-offer="$emit('save-new-student-offer')"
                @submit-with-request-id="$emit('submit-with-request-id')"
            />

            <!-- Hidden Fields -->
            <HiddenFields
                :csrf-token="csrfToken"
                :institute-id="instituteId"
                :course-id="courseId"
                :residences-obj="residencesObj"
                :airports-obj="airportsObj"
                :residence-id="residenceId"
                :airport-id="airportId"
                :data-obj="dataObj"
                :visa-checker="visaChecker"
                :insurance-checker="insuranceChecker"
                :coupon-code="couponCode"
                :is-admin="isAdmin"
                :request-id="requestId"
                :compare-offer="compareOffer"
            />
        </form>
    </div>
</template>

<script>
import StartDatePicker from './form-sections/StartDatePicker.vue';
import CourseWeeksSelector from './form-sections/CourseWeeksSelector.vue';
import ResidenceSection from './form-sections/ResidenceSection.vue';
import AirportSelector from './form-sections/AirportSelector.vue';
import InsuranceSection from './form-sections/InsuranceSection.vue';
import VisaSection from './form-sections/VisaSection.vue';
import AdditionalCosts from './form-sections/AdditionalCosts.vue';
import CouponSection from './form-sections/CouponSection.vue';
import ActionButtons from './form-sections/ActionButtons.vue';
import HiddenFields from './form-sections/HiddenFields.vue';

export default {
    name: 'BookingForm',
    components: {
        StartDatePicker,
        CourseWeeksSelector,
        ResidenceSection,
        AirportSelector,
        InsuranceSection,
        VisaSection,
        AdditionalCosts,
        CouponSection,
        ActionButtons,
        HiddenFields,
    },
    props: {
        dataObj: Object,
        startDate: [Date, String],
        courseWeeks: [String, Number],
        residenceWeeks: [String, Number],
        weeksCount: Number,
        residencesObj: [Array, Object],
        airportsObj: [Array, Object],
        residenceId: [String, Number],
        airportId: [String, Number],
        insuranceChecker: [String, Number],
        visaChecker: [String, Number],
        studentsNumber: [String, Number],
        haveCoupon: [String, Number],
        couponCode: String,
        couponStatus: String,
        compareOffer: Boolean,
        disabledVacationDates: Array,
        postErrors: Object,
        saveRequestUrl: String,
        csrfToken: String,
        instituteId: [String, Number],
        courseId: [String, Number],
        isAdmin: [String, Number],
        requestId: [String, Number],
        addNewStudentRequestObj: [Object, Boolean],
        tra: Object,
        appName: String,
        customResidence: Object,
    },
    emits: [
        'update-start-date',
        'update-course-weeks',
        'update-residence-weeks',
        'update-residence-id',
        'update-airport-id',
        'update-insurance-checker',
        'update-visa-checker',
        'update-students-number',
        'update-have-coupon',
        'update-coupon-code',
        'update-compare-offer',
        'institute-vacation-check',
        'apply-coupon',
        'remove-coupon',
        'invoice',
        'save-new-student-offer',
        'submit-with-request-id',
    ],
};
</script>